/**
 * AJAX cart controls for reusable <product-item> cards.
 * The underlying product form remains a normal Shopify form when JavaScript is unavailable.
 */
(() => {
  if (customElements.get('product-item')) return;

  const cartRoot = window.Shopify?.routes?.root || '/';
  let cartPromise;
  const cartUrl = path => `${cartRoot}${path}`;

  const getCart = (fresh = false) => {
    if (fresh || !cartPromise) {
      cartPromise = fetch(cartUrl('cart.js'), {
        headers: { Accept: 'application/json' }
      }).then(response => {
        if (!response.ok) throw new Error('Unable to load the cart.');
        return response.json();
      });
    }
    return cartPromise;
  };

  const updateHeaderCartBadge = itemCount => {
    document.querySelectorAll('.site-header__cart-link').forEach(cartLink => {
      let badge = cartLink.querySelector('.site-header__cart-count');
      if (itemCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'site-header__cart-count';
          cartLink.appendChild(badge);
        }
        badge.textContent = String(itemCount);
      } else if (badge) {
        badge.remove();
      }
      cartLink.setAttribute('aria-label', `Cart, ${itemCount} items`);
    });
  };

  const publishCart = cart => {
    updateHeaderCartBadge(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  class ProductItem extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cartControlInitialized === 'true') return;
      this.dataset.cartControlInitialized = 'true';

      this.variantId = String(this.dataset.productVariant || '');
      this.form = this.querySelector('[data-product-card-form]');
      this.addButton = this.querySelector('[data-product-card-add]');
      this.quantityControl = this.querySelector('[data-product-card-quantity]');
      this.quantityOutput = this.querySelector('[data-product-card-count]');
      this.minusButton = this.querySelector('[data-product-card-minus]');
      this.plusButton = this.querySelector('[data-product-card-plus]');
      this.status = this.querySelector('[data-product-card-status]');
      this.lineKey = null;
      this.quantity = 0;
      this.pending = false;

      if (
        !this.form ||
        !this.addButton ||
        !this.quantityControl ||
        !this.quantityOutput ||
        !this.minusButton ||
        !this.plusButton ||
        !this.variantId
      ) return;

      this.handleCartUpdated = event => this.sync(event.detail.cart);
      document.addEventListener('cart:updated', this.handleCartUpdated);

      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.setQuantity(this.quantity + 1);
      });
      this.minusButton.addEventListener('click', () => this.setQuantity(this.quantity - 1));
      this.plusButton.addEventListener('click', () => this.setQuantity(this.quantity + 1));

      getCart()
        .then(cart => this.sync(cart))
        .catch(error => this.showError(error));
    }

    disconnectedCallback() {
      if (this.handleCartUpdated) {
        document.removeEventListener('cart:updated', this.handleCartUpdated);
      }
    }

    sync(cart) {
      const matchingLines = cart.items.filter(item => String(item.variant_id) === this.variantId);
      const matchingLine = matchingLines.find(item => {
        const hasProperties = item.properties && Object.keys(item.properties).length > 0;
        return !hasProperties && !item.selling_plan_allocation;
      }) || matchingLines[0];

      this.lineKey = matchingLine ? matchingLine.key : null;
      this.quantity = matchingLine ? matchingLine.quantity : 0;
      this.quantityOutput.textContent = String(this.quantity);
      this.addButton.hidden = this.quantity > 0;
      this.quantityControl.hidden = this.quantity < 1;
      this.clearError();
    }

    setBusy(isBusy) {
      this.pending = isBusy;
      this.addButton.classList.toggle('is-updating', isBusy);
      this.quantityControl.classList.toggle('is-updating', isBusy);
      this.addButton.setAttribute('aria-busy', String(isBusy));
      this.addButton.disabled = isBusy;
      this.minusButton.disabled = isBusy;
      this.plusButton.disabled = isBusy;
    }

    clearError() {
      if (this.status) this.status.textContent = '';
    }

    showError(error) {
      const message = error instanceof Error ? error.message : 'Unable to update the cart.';
      if (this.status) this.status.textContent = message;
      console.error('Unable to update product card quantity.', error);
    }

    async setQuantity(nextQuantity) {
      if (this.pending) return;
      const quantity = Math.max(0, nextQuantity);
      this.setBusy(true);
      this.clearError();

      try {
        let response;
        if (this.lineKey) {
          response = await fetch(cartUrl('cart/change.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: this.lineKey, quantity })
          });
        } else {
          const formData = new FormData(this.form);
          formData.set('quantity', '1');
          response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData
          });
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Unable to update the cart.');
        }

        const cart = await getCart(true);
        publishCart(cart);
      } catch (error) {
        this.showError(error);
      } finally {
        this.setBusy(false);
      }
    }
  }

  customElements.define('product-item', ProductItem);
})();
